import {
  usePlaybackIsPlaying,
  usePlaybackIsSeeking,
  usePlaybackPause,
} from '@app/core/playback/hooks/usePlayback';
import { CursorMenuP } from '@app/modules/logger/screens/stream/components/focus/cursor/cursor-menu/CursorMenuP';
import { useStreamSettingsStore } from '@app/modules/logger/screens/stream/stores/stream-screen-settings.store';
import {
  useClearCursor,
  useCursor,
} from '@app/modules/logger/screens/stream/stores/stream-screen.store';
import {
  useCanEndSelectionWithDurationLimit,
  useCanStartSelectionWithDurationLimit,
} from '@app/modules/logger/screens/stream/stores/useCanSelectionWithDurationLimit';
import { useStreamSelection } from '@app/modules/logger/screens/stream/stores/useStreamSelection';
import { MenuAction } from '@mi-front/ui';
import React from 'react';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { useAuditActionsForLoggerSelectionMenu } from '../../../../../../../audit/modules/station-audit/components/logger-integration/useAuditActionsForLoggerSelectionMenu';
import { useCopyCursorToClipboard } from '../../../../helpers/useCopyCursorToClipboard';
import { usePlayFromCursor } from '../../../../hooks/playback/useStreamPlayback';
import { useOpenFragmentDrawer } from '../../selection/useOpenFragmentDrawer';
import { useCursorMenuCapture } from './useCursorMenuCapture';

export interface LoggerCursorMenuPlugin {
  customActions: MenuAction[];
  components?: React.ReactNode;
}

export const PLUGINS = [useAuditActionsForLoggerSelectionMenu];

export function CursorMenu() {
  const station = useActiveStation();
  const ts = useCursor();
  const isSeeking = usePlaybackIsSeeking();
  const isPlaying = usePlaybackIsPlaying();
  const pause = usePlaybackPause();
  const playFromCursor = usePlayFromCursor();
  const plugins = PLUGINS.map((pluginFn) => pluginFn());
  // Only hide when create-fragment is already open; other drawers (e.g. live-topics) can be replaced.
  const isCreateFragmentDrawerOpen = useStreamSettingsStore(
    (s) => s.drawerType === 'create-fragment',
  );
  const openFragmentDrawer = useOpenFragmentDrawer();
  const { setSelectionStart, setSelectionEnd } = useStreamSelection();
  const canStartSelectionWithLimit = useCanStartSelectionWithDurationLimit();
  const canEndSelectionWithLimit = useCanEndSelectionWithDurationLimit();
  const clearCursor = useClearCursor();
  const copyCursorToClipboard = useCopyCursorToClipboard();
  const capture = useCursorMenuCapture();

  if (!ts) {
    return null;
  }

  const allowStartSelection = canStartSelectionWithLimit(ts);
  const allowEndSelection = canEndSelectionWithLimit(ts);

  return (
    <>
      <CursorMenuP
        loadingAudio={isSeeking}
        play={
          isPlaying
            ? null
            : () => {
                capture('play');
                playFromCursor();
              }
        }
        pause={
          isPlaying
            ? () => {
                capture('pause');
                pause();
              }
            : null
        }
        startSelection={
          allowStartSelection
            ? () => {
                capture('start_selection');
                setSelectionStart(ts);
              }
            : null
        }
        endSelection={
          allowEndSelection
            ? () => {
                capture('end_selection');
                setSelectionEnd(ts);
              }
            : null
        }
        createFragment={
          isCreateFragmentDrawerOpen
            ? null
            : () => {
                capture('create_fragment');
                openFragmentDrawer();
              }
        }
        clear={() => {
          capture('clear');
          clearCursor();
        }}
        copyURL={() => {
          capture('copy_url');
          copyCursorToClipboard(station, ts);
        }}
        cursorTs={ts}
        customActions={plugins.map((p) => p.customActions).flat()}
      />
      {plugins.map((c, index) => (
        <React.Fragment key={index}>{c.components}</React.Fragment>
      ))}
    </>
  );
}
